import { FileConfig } from 'src/const/appConfig';

/**
 * returns if the image resolution exceeds the maximum configured size
 *
 * @param fileToValidate
 * @returns
 */
export const doesImageResolutionExceedMaximum = (
  fileToValidate: File,
): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const image = new Image();
      image.src = event.target?.result as string;
      image.onload = () => {
        resolve(image.width * image.height > FileConfig.PIXEL_LIMIT);
      };
    };

    reader.onerror = () => {
      resolve(false);
    };

    reader.readAsDataURL(fileToValidate);
  });
};

/**
 * returns if the video resolution exceeds the maximum configured size
 *
 * @param fileToValidate
 * @returns true if exceeds the maximum configured size
 */
export const doesVideoResolutionExceedMaximum = (
  fileToValidate: File,
): Promise<boolean> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.src = URL.createObjectURL(fileToValidate);

    const cleanUpResources = () => {
      video.remove();

      URL.revokeObjectURL(video.src);
    };

    video.onloadedmetadata = () => {
      resolve(video.videoWidth * video.videoHeight > FileConfig.PIXEL_LIMIT);
      cleanUpResources();
    };

    video.onerror = () => {
      cleanUpResources();
      resolve(false);
    };
  });
};

/**
 * returns if the file size exceeds the maximum configured size
 *
 * @param fileToValidate
 * @returns true if exceeds the maximum configured size
 */
export const doesFileSizeExceedMaximum = (fileToValidate: File) => {
  const maximumSize = fileToValidate.type.includes('image')
    ? FileConfig.MAX_IMAGE_SIZE_BYTES
    : FileConfig.MAX_VIDEO_SIZE_BYTES;

  return fileToValidate.size > maximumSize;
};

/**
 * returns if the file resolution exceeds the maximum configured size
 *
 * @param fileToValidate
 * @returns true if exceeds the maximum configured size
 */
export const doesFileResolutionExceedMaximum = async (
  fileToValidate: File,
): Promise<boolean> => {
  let exceeds = false;

  // Check max resolution for images and videos
  if (fileToValidate.type.includes('image')) {
    exceeds = await doesImageResolutionExceedMaximum(fileToValidate);
  } else if (fileToValidate.type.includes('video')) {
    exceeds = await doesVideoResolutionExceedMaximum(fileToValidate);
  }

  return exceeds;
};
